import clsx from "clsx";
import { useTranslations } from "use-intl";
import useNavAppSnapshot from "./hooks/useNavAppSnapshot";
import { useStoreState } from "./store/useStore";
import { popperTriggerHover, stylesText } from "./styles";
import { NavAppBridge, NavAppSnapshot } from "./types";

const useFuturesActionsState = (): {
  appBridge: NavAppBridge;
  snapshot: NavAppSnapshot;
} | null => {
  const { appBridge, snapshot } = useNavAppSnapshot();

  if (snapshot?.app !== "futures" || snapshot.page !== "futures-trade" || !appBridge) {
    return null;
  }

  return { appBridge, snapshot };
};

export const FuturesModeSwitch = () => {
  const { theme } = useStoreState();
  const state = useFuturesActionsState();

  if (!state) {
    return null;
  }

  const { appBridge, snapshot } = state;
  const tradeMode = snapshot.futures?.tradeMode ?? "standard";
  const canShowCopyTrade = !!snapshot.futures?.canShowCopyTrade;
  const hasSwitchAction = !!appBridge.actions?.switchFuturesTradeMode;
  const labels = snapshot.futures?.tradeModeLabels;
  const regularText = labels?.regular ?? "Regular";
  const copyText = labels?.copy ?? "Copy";

  if (!canShowCopyTrade || !hasSwitchAction) {
    return null;
  }

  return (
    <div
      className={clsx(
        "mr-[16px] flex h-[24px] items-center rounded-[4px] p-px max-[860px]:hidden",
        theme === "dark" ? "bg-dark-fill-secondary" : "bg-light-fill-secondary"
      )}
    >
      <button
        type="button"
        className={clsx(
          "flex h-[22px] cursor-pointer items-center rounded-[4px] border-0 bg-transparent px-[12px] py-0 text-[12px] font-medium leading-[18px]",
          tradeMode !== "copyTrade"
            ? theme === "dark"
              ? "bg-dark-fill-tertiary text-dark-label"
              : "bg-light-fill-tertiary text-light-label"
            : theme === "dark"
              ? "text-dark-label-40 hover:text-dark-label"
              : "text-light-label-40 hover:text-light-label"
        )}
        onClick={() => appBridge.actions?.switchFuturesTradeMode?.("standard")}
      >
        {regularText}
      </button>
      <button
        type="button"
        className={clsx(
          "flex h-[22px] cursor-pointer items-center rounded-[4px] border-0 bg-transparent px-[12px] py-0 text-[12px] font-medium leading-[18px]",
          tradeMode === "copyTrade"
            ? theme === "dark"
              ? "bg-dark-fill-tertiary text-dark-label"
              : "bg-light-fill-tertiary text-light-label"
            : theme === "dark"
              ? "text-dark-label-40 hover:text-dark-label"
              : "text-light-label-40 hover:text-light-label"
        )}
        onClick={() => appBridge.actions?.switchFuturesTradeMode?.("copyTrade")}
      >
        {copyText}
      </button>
    </div>
  );
};

export const FuturesSettingsAction = () => {
  const { theme } = useStoreState();
  const state = useFuturesActionsState();
  const t = useTranslations("header");

  if (!state?.appBridge.actions?.openFuturesSettings) {
    return null;
  }

  return (
    <button
      type="button"
      aria-label={t("settings")}
      title={t("settings")}
      className={clsx(
        "ml-[24px] flex h-[48px] cursor-pointer items-center border-0 bg-transparent p-0",
        stylesText({ mode: theme }),
        popperTriggerHover({ mode: theme })
      )}
      onClick={() => state.appBridge.actions?.openFuturesSettings?.()}
    >
      <span aria-hidden className="iconfont icon-setting-fill text-[20px] leading-none" />
    </button>
  );
};

export default FuturesSettingsAction;
